import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { object, string, number, bool, date } from "yup";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";

import api from "../../../../providers/api";
import { addToast, monthDiff } from "../../../../helpers";
import AdminFormModal from "../../../AdminFormModal";

import "./style.scss";

const { Field } = Form;
const today = new Date();
const oneDay = 24 * 60 * 60 * 1000;
today.setHours(0, 0, 0, 0);

const validationSchema = object().shape({
  ArabicName: string().required(" "),
  EnglishName: string().required(" "),
  ExecutiveSummary: string().required(" "),
  InScope: string().required(" "),
  Department: object().required(" ").nullable(),
  OwnerModel: object().required(" ").nullable(),
  SponsorModel: object().required(" ").nullable(),
  ManagerModel: object().required(" ").nullable(),
  EPMOModel: object().required(" ").nullable(),
  Portfolio: object().required(" ").nullable(),
  Program: object().nullable(),
  Duration: number().min(0, " "),
  Budget: number().min(0, " "),
  Weight: number().min(0, " "),
  StartDate: date().required(" "),
  FinishDate: date()
    .required(" ")
    .when(["StartDate"], (startDate, schema) => {
      if (startDate) {
        startDate.setHours(0, 0, 0, 0);
        return schema.test("is-greater-than-start-date", "", async (value) => {
          return (value - startDate) / oneDay >= 1;
        });
      }
    })
    .nullable(),
  IsTechnical: bool()
});

const ProjectForm = ({
  data,
  closeModal,
  projects,
  setProjects,
  lists,
  allWeights,
  initiativeData = null,
  getData = () => {}
}) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  let totalWeight = allWeights;
  if (data?.Weight) totalWeight = totalWeight - data?.Weight;

  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    ArabicName: "",
    EnglishName: "",
    ExecutiveSummary: "",
    InScope: "",
    Department: null,
    OwnerModel: null,
    SponsorModel: null,
    ManagerModel: null,
    EPMOModel: null,
    Portfolio: null,
    Program: null,
    Duration: 0,
    Weight: 0,
    Budget: 0,
    StartDate: new Date(),
    FinishDate: new Date(),
    IsTechnical: "false"
  });

  useEffect(() => {
    if (data) {
      setInitialValues({
        ...data,
        IsTechnical: `${data?.IsTechnical}`,
        StartDate: new Date(data.StartDate),
        FinishDate: new Date(data.FinishDate)
      });
    }
    setLoading(false);
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    const validateRes = await api.initiatives.validateProject({
      EnglishName: values.EnglishName,
      ProjectUID: values?.ProjectUID || null
    });

    if (!initiativeData) {
      const isInvalidName = projects.some(
        (e) => e.EnglishName === values.EnglishName && e.ID !== data?.ID
      );

      if (isInvalidName) {
        addToast.error(
          intl.formatMessage({ id: "common.errorCreateProject" }),
          {
            toastId: "get-users-error"
          }
        );
        return;
      }
    }

    if (validateRes?.StatusCode === "Success") {
      let body = {
        ...values,
        StartDate: new Date(
          values.StartDate + new Date(values.StartDate).getTimezoneOffset()
        ).toISOString(),
        FinishDate: new Date(
          values.FinishDate + new Date(values.FinishDate).getTimezoneOffset()
        ).toISOString(),
        Duration: monthDiff(values.StartDate, values.FinishDate),
        ID: data ? data.ID : projects?.length + 1
      };

      if (initiativeData) {
        body = {
          ...body,
          initiative: { ID: initiativeData.ID },
          ID: data?.ID,
          Statement: "",
          CostAnalysis: "",
          DeliveryStrategy: ""
        };
        try {
          const service = data ? "updateProject" : "createProject";
          const res = await api.initiatives[service](body);
          const { StatusCode } = res;
          if (StatusCode === "Success") {
            addToast.success(
              data ? "toast.successUpdate" : "toast.successSave"
            );
            getData();
            closeModal();
          }
        } catch (error) {
          addToast.error(error?.message, {
            toastId: "get-users-error"
          });
        } finally {
          setSubmitting(false);
        }
      } else if (data) {
        const newData = [...projects];
        const index = projects.findIndex((e) => e.ID === data.ID);
        newData[index] = body;
        setProjects([...newData]);
      } else setProjects([...projects, body]);
      setSubmitting(false);
      closeModal();
    }
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({
        id: data ? "initiative.update-project" : "initiative.add-project"
      })}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="performance-indicator-form-body"
            >
              <Space direction="vertical" className="container" size="large">
                <Row size="large">
                  <Field
                    name="EnglishName"
                    label={intl.formatMessage({
                      id: "project.english-name"
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "project.english-name"
                      })
                    }}
                    disabled={initiativeData && data}
                    hideErrorMessage
                    required
                  />
                  <Field
                    name="ArabicName"
                    label={intl.formatMessage({ id: "project.arabic-name" })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "project.arabic-name"
                      })
                    }}
                    disabled={initiativeData && data}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="large">
                  <Field
                    type="textarea"
                    name="ExecutiveSummary"
                    label={intl.formatMessage({
                      id: "project.executive-summary"
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "project.executive-summary"
                      })
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="textarea"
                    name="InScope"
                    label={intl.formatMessage({
                      id: "project.work-scope"
                    })}
                    inputProps={{
                      placeholder: intl.formatMessage({
                        id: "project.work-scope"
                      })
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="large">
                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "project.owner"
                    })}
                    name="OwnerModel"
                    inputProps={{
                      options: lists?.users,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      placeholder: intl.formatMessage({
                        id: "project.owner"
                      })
                    }}
                    disabled={initiativeData && data}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "project.sponsor"
                    })}
                    name="SponsorModel"
                    inputProps={{
                      options: lists?.users,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      placeholder: intl.formatMessage({
                        id: "project.sponsor"
                      })
                    }}
                    disabled={initiativeData && data}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="large">
                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "project.portfolio"
                    })}
                    name="Portfolio"
                    inputProps={{
                      options: lists?.portfolios,
                      getOptionLabel: (el) =>
                        isAr ? el.ArabicName : el.EnglishName,
                      getOptionValue: (el) => el?.ProjectUID,
                      placeholder: intl.formatMessage({
                        id: "project.portfolio"
                      })
                    }}
                    disabled={initiativeData && data}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "project.portfolio-manager"
                    })}
                    name="portfolioManager"
                    disabled={true}
                    inputProps={{
                      options: lists?.users,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      value: values.Portfolio?.ManagerModel,
                      placeholder: intl.formatMessage({
                        id: "project.portfolio-manager"
                      })
                    }}
                    hideErrorMessage
                  />
                </Row>
                <Row size="large">
                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "common.department"
                    })}
                    name="Department"
                    inputProps={{
                      options: lists?.departments,
                      getOptionLabel: (el) =>
                        isAr ? el.ArabicName : el.EnglishName,
                      getOptionValue: (el) => el?.ID,
                      placeholder: intl.formatMessage({
                        id: "common.department"
                      })
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>
                <Row size="large">
                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "project.program"
                    })}
                    name="Program"
                    inputProps={{
                      options: lists?.programs,
                      isClearable: true,
                      getOptionLabel: (el) =>
                        isAr ? el.ArabicName : el.EnglishName,
                      getOptionValue: (el) => el?.ID,
                      placeholder: intl.formatMessage({
                        id: "project.program"
                      })
                    }}
                    hideErrorMessage
                  />

                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "program.owner"
                    })}
                    name="ProgramManager"
                    disabled={true}
                    inputProps={{
                      options: lists?.users,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      value: values.Program?.OwnerModel,
                      placeholder: intl.formatMessage({
                        id: "program.owner"
                      })
                    }}
                    hideErrorMessage
                  />
                </Row>

                <Row size="large">
                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "project.manager"
                    })}
                    name="ManagerModel"
                    inputProps={{
                      options: lists?.users,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      placeholder: intl.formatMessage({
                        id: "project.manager"
                      })
                    }}
                    disabled={initiativeData && data}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="select"
                    label={intl.formatMessage({
                      id: "project.controller"
                    })}
                    name="EPMOModel"
                    inputProps={{
                      options: lists?.epmoUsers,
                      getOptionLabel: (el) => el.DisplayName,
                      getOptionValue: (el) => el?.UserName,
                      placeholder: intl.formatMessage({
                        id: "project.controller"
                      })
                    }}
                    disabled={initiativeData && data}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="large">
                  <Field
                    type="date"
                    name="StartDate"
                    label={intl.formatMessage({ id: "project.start-date" })}
                    hideErrorMessage
                    required
                  />
                  <Field
                    type="date"
                    name="FinishDate"
                    inputProps={{
                      minDate: values.StartDate || today,
                      error: values.FinishDate - values.StartDate < oneDay
                    }}
                    label={intl.formatMessage({ id: "project.end-date" })}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="large">
                  <Field
                    type="number"
                    name="Duration"
                    label={intl.formatMessage({ id: "project.duration" })}
                    inputProps={{
                      onKeyDown: (evt) => {
                        if (
                          evt.key === "e" ||
                          evt.key === "-" ||
                          evt.key === "+"
                        ) {
                          evt.preventDefault();
                        }
                      },
                      value: monthDiff(values.StartDate, values.FinishDate),
                      placeholder: intl.formatMessage({
                        id: "project.duration"
                      })
                    }}
                    disabled={true}
                    hideErrorMessage
                  />

                  <Field
                    type="number"
                    name="Budget"
                    label={intl.formatMessage({ id: "project.budget" })}
                    inputProps={{
                      onKeyDown: (evt) => {
                        if (
                          evt.key === "e" ||
                          evt.key === "-" ||
                          evt.key === "+"
                        ) {
                          evt.preventDefault();
                        }
                      },
                      placeholder: intl.formatMessage({
                        id: "project.budget"
                      })
                    }}
                    hideErrorMessage
                    required
                  />
                </Row>

                <Row size="large">
                  <Field
                    type="number"
                    name="Weight"
                    label={intl.formatMessage({ id: "project.weight" })}
                    inputProps={{
                      onKeyDown: (evt) => {
                        if (
                          evt.key === "e" ||
                          evt.key === "-" ||
                          evt.key === "+"
                        ) {
                          evt.preventDefault();
                        }
                      },
                      error: totalWeight + parseInt(values.Weight) > 100,
                      placeholder: intl.formatMessage({
                        id: "project.weight"
                      })
                    }}
                    hideErrorMessage
                    required
                  />

                  <Field
                    type="radio-group"
                    name="IsTechnical"
                    label={intl.formatMessage({ id: "project.is-technical" })}
                    span={6}
                    disabled={initiativeData && data}
                    inputProps={{
                      options: [
                        {
                          value: "true",
                          label: isAr ? "نعم" : "Yes"
                        },
                        {
                          value: "false",
                          label: isAr ? "لا" : "No"
                        }
                      ],
                      direction: "horizontal"
                    }}
                  />
                </Row>
              </Space>

              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={
                    !isValid || totalWeight + parseInt(values.Weight) > 100
                  }
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id: data ? "common.update" : "common.add"
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default ProjectForm;
